import React, { useEffect, useState } from 'react';
import { Sparkles, Ghost } from 'lucide-react';
import { api } from '@/lib/api';

/**
 * SaleBanner
 * ─────────────────────────────────────────────────────────
 * Slim promo strip pinned to the top of the site while a sale
 * is running. Auto-hides when the sale expires or the
 * `/api/sale/current` endpoint says { active: false }.
 */
export default function SaleBanner() {
  const [sale, setSale] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let cancelled = false;
    api.get('/sale/current')
      .then((r) => { if (!cancelled) setSale(r.data); })
      .catch(() => { if (!cancelled) setSale({ active: false }); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!sale?.active) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [sale]);

  if (!sale?.active) return null;
  const endsAt = new Date(sale.ends_at).getTime();
  const remaining = Math.max(0, endsAt - now);
  if (remaining <= 0) return null;
  const hh = Math.floor(remaining / 3_600_000);
  const mm = Math.floor((remaining % 3_600_000) / 60_000);
  const ss = Math.floor((remaining % 60_000) / 1000);

  // Build a dynamic headline from the sale shape
  const scope =
    sale.applies_to === 'vip' ? 'Premium VIP' :
    sale.applies_to === 'product' ? 'every script' :
    'every script & VIP tier';
  const amount = sale.type === 'fixed'
    ? `$${Math.round(sale.value)} OFF`
    : `${Math.round(sale.value)}% OFF`;

  return (
    <div
      data-testid="sale-banner"
      className="relative overflow-hidden border-b border-brand/40 bg-gradient-to-r from-[#0b1220] via-[#0a1424] to-[#0b1220]"
    >
      <div
        className="absolute inset-y-0 -left-8 -right-8 pointer-events-none opacity-40"
        style={{
          background:
            'radial-gradient(circle at 30% 50%, rgb(var(--brand-rgb)/0.35), transparent 55%),' +
            'radial-gradient(circle at 70% 50%, rgba(255,255,255,0.06), transparent 60%)',
        }}
      />
      <div className="relative mx-auto max-w-7xl px-4 py-2 flex items-center justify-center gap-3 md:gap-5 flex-wrap text-center">
        <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-brand/15 border border-brand/40 text-brand text-[10px] uppercase tracking-widest font-mono font-semibold px-3 py-1">
          {sale.theme === 'halloween' ? <><Ghost className="h-3 w-3" /> Halloween sale</> : <><Sparkles className="h-3 w-3" /> Flash sale</>}
        </span>
        <span className="text-white text-sm md:text-[15px]">
          <span className="font-semibold text-brand">{amount}</span>{' '}
          <span className="text-white/85">{scope}</span>
          <span className="text-white/50"> · auto-applied at checkout</span>
        </span>
        <span
          className="inline-flex items-center gap-2 rounded-full bg-black/40 border border-white/10 px-3 py-1 font-mono text-xs md:text-sm text-white"
          aria-label="Sale ends in"
        >
          ends in
          <span className="text-brand font-semibold tabular-nums">
            {String(hh).padStart(2, '0')}:{String(mm).padStart(2, '0')}:{String(ss).padStart(2, '0')}
          </span>
        </span>
      </div>
    </div>
  );
}
